import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/vue-query';
import type { MutationObserverOptions } from '@tanstack/vue-query';
import type { ApiError } from '@/api/errors';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import useQueryVModelFactory from '@@/packages/shared/composables/query-utils/useQueryVmodel';
import {
  FilterComparator,
  FilterOperator,
  SortDirection,
  isGroupingFilterExpression,
  type DatasetRequest,
  type DatasetResponse,
  type FilterExpression,
  type PaginationRequest,
  type SortOption,
} from '@@/packages/shared/libs/search/types';
import { fetchOrgStats as fetchClientNetworkOrgStats } from '@/modules/client-network/api';
import { ASSOCIATED_ACTIVITY_QUERY } from '@/modules/activity-feed/queryKeys';
import suppliersApi from '@/api/suppliers';
import useHasPermissions from '@/modules/current-user/permissions';
import { refreshAssessmentReviewEventually } from '@/modules/profile/api/assessmentReview';
import { request } from '@/api/request';
import type { RiskNote } from '@@/packages/shared/types/models/riskNote';
import type { Risk, RiskSettings, RiskLevel, RiskRange } from '../types';
import {
  RISK_QUERY,
  RISKS_QUERY,
  RISKS_MATRIX_QUERY,
  RISKS_MATRIX_SCORE_QUERY,
  RISKS_SETTINGS_QUERY,
  RISK_NOTES_QUERY,
  SUPPLIERS_BASIC_QUERY,
} from './queryKeys';

export function useRisk(riskNumber: MaybeRefOrGetter<number | null>, options?: QueryOptions) {
  const { hasAnyOfPermissions, Permission } = useHasPermissions();

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [RISK_QUERY, toRef(riskNumber)] as const,
      async queryFn({ queryKey }) {
        if (!queryKey[1]) return undefined;
        const res = await request<Risk>('GET', `/risks/${queryKey[1]}`);
        return res.data;
      },
      enabled: () => !!toValue(riskNumber) && hasAnyOfPermissions(Permission.ClientsRisksView),
    }),
  );

  return { ...query, risk: query.data };
}

type OpenRiskVariables = {
  name: string;
  targetOrgID?: string;
  associatedType?: string;
  associatedID?: string;
  description: string;
  uploads: string[];
  riskType: string;
  riskMatrix?: {
    axisValues: {
      xValueID?: string | null;
      yValueID?: string | null;
    };
  } | null;
};

export function useCreateRisk(options?: MutationOptions<Risk, OpenRiskVariables>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(risk) {
        const res = await request<Risk>('POST', '/risks', risk);
        return res.data;
      },
      async onSuccess(risk, variables) {
        // Opening a risk on a control/answer auto-dismisses its AI finding.
        if (
          variables.targetOrgID &&
          (variables.associatedType === 'control' || variables.associatedType === 'answer')
        ) {
          void refreshAssessmentReviewEventually(queryClient, variables.targetOrgID);
        }
        await queryClient.setQueryData([RISK_QUERY, risk.number], risk);
        await queryClient.invalidateQueries({ queryKey: [RISKS_QUERY] });
      },
    }),
  );
}

export function useUpdateRisk(options?: MutationOptions<Risk, Risk>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(riskData) {
        const res = await request<Risk>('PUT', `/risks/${riskData.number}`, riskData);
        return res.data;
      },
      async onSuccess(risk) {
        await queryClient.setQueryData([RISK_QUERY, risk.number], risk);
        await queryClient.invalidateQueries({ queryKey: [RISKS_QUERY] });
      },
    }),
  );
}

export function useCloseRisk(options?: MutationOptions<Risk, number>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(riskNumber) {
        const res = await request<Risk>('POST', `/risks/${riskNumber}/close`);
        return res.data;
      },
      async onSuccess(risk) {
        await queryClient.setQueryData([RISK_QUERY, risk.number], risk);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [RISKS_QUERY] }),
          queryClient.invalidateQueries({
            queryKey: [ASSOCIATED_ACTIVITY_QUERY, { associatedID: risk.id }],
          }),
        ]);
      },
    }),
  );
}

export function useReopenRisk(options?: MutationOptions<Risk, number>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(riskNumber) {
        const res = await request<Risk>('POST', `/risks/${riskNumber}/reopen`);
        return res.data;
      },
      async onSuccess(risk) {
        await queryClient.setQueryData([RISK_QUERY, risk.number], risk);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [RISKS_QUERY] }),
          queryClient.invalidateQueries({
            queryKey: [ASSOCIATED_ACTIVITY_QUERY, { associatedID: risk.id }],
          }),
        ]);
      },
    }),
  );
}

export function useDeleteRisk(options?: MutationOptions<Risk, number>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(riskNumber) {
        const res = await request<Risk>('DELETE', `/risks/${riskNumber}`);
        return res.data;
      },
      async onSuccess(risk) {
        queryClient.removeQueries({ queryKey: [RISK_QUERY, risk.number] });
        await queryClient.invalidateQueries({ queryKey: [RISKS_QUERY] });
      },
    }),
  );
}

export type RisksResponse = DatasetResponse & {
  risks: Risk[];
  totalRisks: number;
};

export const fetchRisks = (
  filter?: FilterExpression,
  sort?: SortOption[],
  page?: PaginationRequest | null,
) =>
  request<RisksResponse>('POST', '/risks/list', {
    // Nested filter needs operands for backend validation to succeed.
    // Send no filter when attempting to send a nested filter with no operands.
    filter:
      filter && isGroupingFilterExpression(filter) && filter?.operands?.length === 0
        ? undefined
        : filter,
    sort,
    page,
  });

export function useRisks(params: MaybeRefOrGetter<DatasetRequest>, options?: QueryOptions) {
  const { hasAnyOfPermissions, Permission } = useHasPermissions();

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [RISKS_QUERY, toRef(params)] as const,
      async queryFn({ queryKey }) {
        const res = await fetchRisks(queryKey[1].filter, queryKey[1].sort, queryKey[1].page);
        return res.data;
      },
      enabled: () => hasAnyOfPermissions(Permission.ClientsRisksView),
    }),
  );

  const risks = computed(() => query.data.value?.risks ?? []);
  const totalRisks = computed(() => query.data.value?.totalRisks ?? 0);
  const page = computed(() => query.data.value?.page ?? null);

  return { ...query, risks, totalRisks, page };
}

/**
 * This composable is used in a couple places for supplier profiles - it's
 * defined in a single place to ensure that both usages are the same and the
 * query is reused.
 */
export function useSupplierOpenRisksOnControlsAndProductAnswers(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  // Please don't raise this number without reading the comment below and
  // considering the performance impacts
  const LAZY_PAGE_LIMIT = 150;

  const query = useRisks(
    () => ({
      sort: [{ field: 'number', direction: SortDirection.Descending }],
      filter: {
        operator: FilterOperator.And,
        operands: [
          {
            field: 'targetOrgID',
            comparator: FilterComparator.Equal,
            value: toValue(supplierID),
          },
          {
            field: 'riskType',
            comparator: FilterComparator.Equal,
            value: 'control',
          },
          {
            field: 'status',
            comparator: FilterComparator.Equal,
            value: 'OPEN',
          },
        ],
      },
      page: { limit: LAZY_PAGE_LIMIT, offset: 0 },
    }),
    options,
  );

  // We're using some lazy logic above to get around pagination, so this check
  // will throw a warning if an org hits that limit and the lazy logic breaks.
  // A better(?) solution would be to use useInfiniteQuery and load smaller
  // chunks of data in multiple requests
  watch(query.risks, (risks) => {
    if (risks.length === LAZY_PAGE_LIMIT) {
      logError('Pagination limit reached');
    }
  });

  // Risks for org level will use ControlID, product level will use AnswerID
  const risksByAssociatedID = computed(() =>
    Object.groupBy(query.risks.value, (risk) => risk.associatedID || 'unassociated'),
  );

  return {
    ...query,
    query,
    risksByAssociatedID,
  };
}

export type RiskLevelsResponse = {
  axes: {
    x: { label: string; axis: RiskLevel[] };
    y: { label: string; axis: RiskLevel[] };
  };
  calculations: {
    [key: string]: number;
  };
  ranges: RiskRange[];
};

export function useRiskLevels(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [RISKS_MATRIX_QUERY],
      async queryFn() {
        const res = await request<RiskLevelsResponse>('GET', '/risks/matrix');
        return res.data;
      },
    }),
  );

  const likelihoodList = computed<RiskLevel[]>(() => query.data.value?.axes.y.axis ?? []);
  const impactList = computed<RiskLevel[]>(() => query.data.value?.axes.x.axis ?? []);
  const scoreCalculations = computed(() => query.data.value?.calculations ?? {});
  const impactFilterOptions = computed(() =>
    toValue(impactList) != null
      ? toValue(impactList).map((impact) => ({
          name: impact.label,
          value: impact.id,
        }))
      : [],
  );

  const likelihoodFilterOptions = computed(() =>
    toValue(likelihoodList) != null
      ? toValue(likelihoodList).map((likelihood) => ({
          name: likelihood.label,
          value: likelihood.id,
        }))
      : [],
  );

  function getRiskScore(impact: RiskLevel, likelihood: RiskLevel): number | undefined {
    return scoreCalculations.value[`${impact.id}-${likelihood.id}`];
  }

  return {
    ...query,
    likelihoodList,
    impactList,
    getRiskScore,
    impactFilterOptions,
    likelihoodFilterOptions,
  };
}

export type UpdateRiskLabelVariables = {
  id: string;
  axis: string;
  label: string;
  position: number;
}[];

export function useUpdateRiskLabel(
  options?: MutationOptions<RiskLevelsResponse, UpdateRiskLabelVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(riskLabel) {
        const res = await request<RiskLevelsResponse>(
          'PUT',
          '/risks/matrix/axis-values',
          riskLabel,
        );
        return res.data;
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [RISKS_MATRIX_QUERY] });
      },
    }),
  );
}

type CreateRiskLabelVariables = {
  axis: string;
  label: string;
  position: number;
}[];

export function useCreateRiskLabel(
  options?: MutationOptions<RiskLevelsResponse, CreateRiskLabelVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(riskLabel) {
        const res = await request<RiskLevelsResponse>(
          'POST',
          '/risks/matrix/axis-values',
          riskLabel,
        );
        return res.data;
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [RISKS_MATRIX_QUERY] });
      },
    }),
  );
}

export function useDeleteRiskLabel(options?: MutationOptions<RiskLevelsResponse, string>) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(riskLabelId) {
        const res = await request<RiskLevelsResponse>(
          'DELETE',
          `/risks/matrix/axis-values/${riskLabelId}`,
        );
        return res.data;
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [RISKS_MATRIX_QUERY] });
      },
    }),
  );
}

export function useRiskSettings(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [RISKS_SETTINGS_QUERY],
      async queryFn() {
        const response = await request<RiskSettings>('GET', '/risks/settings');
        return response.data;
      },
    }),
  );
  return query;
}

function useUpdateRiskSettings(
  options?: MutationObserverOptions<
    RiskSettings,
    ApiError,
    RiskSettings,
    { previousSettings: RiskSettings | undefined }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(settings) {
        const res = await request<RiskSettings>('PUT', '/risks/settings', settings);
        return res.data;
      },
      async onMutate(settings) {
        await queryClient.cancelQueries({ queryKey: [RISKS_SETTINGS_QUERY] });
        const previousSettings = queryClient.getQueryData<RiskSettings>([RISKS_SETTINGS_QUERY]);
        queryClient.setQueryData<RiskSettings>([RISKS_SETTINGS_QUERY], settings);
        return { previousSettings };
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [RISKS_MATRIX_QUERY] });
      },
      async onError(_error, _settings, context) {
        await queryClient.setQueryData([RISKS_SETTINGS_QUERY], context?.previousSettings);
      },
    }),
  );
}

export const useRiskSettingsVModel = useQueryVModelFactory(useRiskSettings, useUpdateRiskSettings);

type RiskMatrixAxisVariables = {
  xAxis: string | undefined | null;
  yAxis: string | undefined | null;
};

type RiskMatrixScoreResponse = {
  score: number;
};

export function useRiskMatrixScore(
  axisValues: MaybeRefOrGetter<RiskMatrixAxisVariables>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [RISKS_MATRIX_SCORE_QUERY, toRef(axisValues)] as const,
      async queryFn({ queryKey }) {
        const { xAxis, yAxis } = queryKey[1];
        if (!xAxis || !yAxis) return { score: null };
        const res = await request<RiskMatrixScoreResponse>('GET', '/risks/matrix/score', null, {
          xAxisValueID: xAxis,
          yAxisValueID: yAxis,
        });
        return res.data;
      },
    }),
  );

  const riskScore = computed(() => query.data.value?.score ?? null);

  return { ...query, riskScore };
}

// TODO: why on earth is this in the risks module? (15 minutes)
export function useOrgs(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIERS_BASIC_QUERY],
      async queryFn() {
        const res = await suppliersApi.fetchSuppliersBasicList();
        return res.data;
      },
    }),
  );

  return { ...query, orgs: query.data };
}

export function useConcentrationRiskStats(
  orgId: MaybeRefOrGetter<string>,
  currentMode: MaybeRefOrGetter<string | null>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: computed(() => ['/client-network/organisations', toValue(orgId), 'stats']),
      async queryFn({ queryKey: queryKeyUntyped }) {
        const queryKey = queryKeyUntyped as [string, string, string];
        const res = await fetchClientNetworkOrgStats(queryKey[1]);
        return res.data;
      },
      ...options,
      enabled: () =>
        toValue(currentMode) === 'client' && toValue(toValue(options)?.enabled) !== false,
    }),
  );

  return { ...query, orgStats: query.data };
}

export const fetchRiskNotes = (number: number) =>
  request<RiskNote[]>('GET', `/risks/${number}/notes`);

const riskNotesQueryFn = async (riskNumber: number) => (await fetchRiskNotes(riskNumber)).data;

export function useRiskNotes(riskNumber: MaybeRefOrGetter<number>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [RISK_NOTES_QUERY, toRef(riskNumber)] as const,
      queryFn: ({ queryKey }) => riskNotesQueryFn(toValue(queryKey[1])),
    }),
  );

  return { ...query, notes: computed(() => query.data.value || []) };
}

// Shares its cache with `useRiskNotes` (same query key), so individual
// side-panel views and bulk callers reuse each other's data.
export function useRiskNotesByRiskNumbers(
  riskNumbers: MaybeRefOrGetter<number[]>,
  options?: QueryOptions,
) {
  const queries = useQueries({
    queries: computed(() =>
      toValue(riskNumbers).map((n) =>
        mergeQueryOptions(options, {
          queryKey: [RISK_NOTES_QUERY, n] as const,
          queryFn: ({ queryKey }) => riskNotesQueryFn(queryKey[1]),
        }),
      ),
    ),
  });

  const isLoading = computed(() => {
    // Treat "queries not yet aligned with the requested numbers" as loading,
    // so callers using `until(isLoading).toBe(false)` don't resolve on the
    // empty initial state right after the input updates.
    if (queries.value.length !== toValue(riskNumbers).length) return true;
    return queries.value.some((q) => q.isPending);
  });
  const isError = computed(() => queries.value.some((q) => q.isError));

  const notesByRiskNumber = computed(() =>
    Object.fromEntries(toValue(riskNumbers).map((n, i) => [n, queries.value[i]?.data ?? []])),
  );

  return { isLoading, isError, notesByRiskNumber };
}

export const fetchRiskOwners = () => request<string[]>('GET', '/risks/owners');

export const createRiskNote = (note: any, number: number) =>
  request('POST', `/risks/${number}/notes/create`, note);

export const fetchRiskMatrix = () => request<RiskLevelsResponse>('GET', '/risks/matrix');
